import type {
    Arbeitsschritt, EinweisungQrQuelle, Gefaehrdung, Schutzmassnahme, SigeBlock, SigeProject,
} from './types';
import { stabilerInhaltsHash, type AblageStand } from '../../services/ablageHilfen';
import type { AusgabeAnker } from '../../services/unterlagenAblage';

/**
 * Der SiGe-Plan in der zentralen Ablage (Stufe 1A, Entscheid E17).
 *
 * WANN abgelegt wird, entscheiden Editor und Vorschau; hier steht, WAS
 * abgelegt wird — Titel, Dateiname, Stand, Fassungskennung — rein und ohne
 * DOM getestet. Den Weg in die Ablage kennt `planAblageAnschluss.ts`.
 *
 * ═══ WAS BEIM SIGE-PLAN EINE FASSUNG IST ══════════════════════════════════
 * Der Plan kennt keinen Freigabeschritt, der etwas festschreibt: `status =
 * 'geprueft'` sperrt nichts, und „Neue Version erstellen" kopiert den Baum in
 * eine zweite, weiter bearbeitbare Zeile. Eine Fassung ist deshalb der
 * INHALTSSTAND einer Version — alles, was auf dem Blatt landet, samt Breite
 * und Schrift, mit denen gedruckt wurde. Zwei Ablagen desselben Standes
 * erkennt die Ablage ueber dieselbe Kennung (`fassung_ref`) und legt nicht
 * zweimal ab.
 *
 * ═══ DREI ANLAESSE ═════════════════════════════════════════════════════════
 *   'versand'  — der PDF-Export MIT Leistungsbuchung. Das ist der Moment, in
 *                dem der Plan als fertig gilt und hinausgeht; abgelegt wird
 *                automatisch. Ein Export ohne Buchung ist ein Zwischenstand
 *                und legt nichts ab.
 *   'freigabe' — Knopf in der Vorschau bei `status = 'geprueft'`: die
 *                geprueft gekennzeichnete Fassung von Hand ablegen.
 *   'entwurf'  — derselbe Knopf bei jedem anderen Status, deutlich als
 *                Entwurf beschriftet.
 *
 * Der Generator kennt den Anlass nicht: ein als Entwurf abgelegter Stand und
 * sein spaeterer Versand sind dasselbe Blatt mit derselben Kennung. Der
 * Versand findet deshalb die Entwurfszeile, und die Ablage beschriftet sie
 * zur versendeten Fassung um (`entwurfsBefoerderung` in unterlagenAblage.ts).
 */

export type SigePlanAblageAnlass = 'freigabe' | 'versand' | 'entwurf';

const text = (v?: string | null): string => (v == null ? '' : String(v)).trim();

/**
 * Der Dateiname des Plans — derselbe fuer Download, Teilen und Ablage.
 *
 * Der heruntergeladene und der abgelegte Plan muessen gleich heissen, sonst
 * liegen im Posteingang und in der Ablage zwei Dateien, denen niemand
 * ansieht, dass sie dasselbe Blatt sind. Zeichen, die in Dateinamen und
 * Speicherpfaden stoeren (Schraegstrich in „12/2026"), werden ersetzt.
 */
export const sigePlanDateiname = (plan: Pick<SigeProject, 'plan_nr'>): string => {
    const nr = text(plan?.plan_nr).replace(/[^\wÄÖÜäöüß\-.]+/g, '_').replace(/^_+|_+$/g, '');
    return `SiGe-Plan_${nr || 'Entwurf'}.pdf`;
};

/**
 * Welcher Anlass gilt fuer den Knopf in der Vorschau?
 *
 * Nur `geprueft` ist eine Fassung; `entwurf` und `ueberarbeitet` sind
 * Arbeitsstaende. Der Versand haengt nicht am Status, sondern am Export mit
 * Buchung — ihn setzt der Editor selbst.
 */
export const sigePlanAblageAnlass = (
    status: SigeProject['status'] | null | undefined,
): Extract<SigePlanAblageAnlass, 'freigabe' | 'entwurf'> =>
    (status === 'geprueft' ? 'freigabe' : 'entwurf');

const STATUS_TEXT: Record<SigeProject['status'], string> = {
    entwurf: 'Entwurf',
    geprueft: 'geprüft',
    ueberarbeitet: 'überarbeitet',
};

/** Der Status, wie er auf dem Plankopf steht. Unbekannte Werte unveraendert. */
export const sigePlanStatusText = (status: string | null | undefined): string => {
    const s = text(status);
    return (STATUS_TEXT as Record<string, string>)[s] || s;
};

/** „TT.MM.JJJJ" aus einem ISO-Tag; ein schon deutsches Datum bleibt stehen. */
export const tagDeutsch = (wert?: string | null): string => {
    const t = text(wert);
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
    if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(t)) return t;
    return '';
};

// ═══════════════════════════════════════════════════════════════════════════
// INHALTSSTAND UND FASSUNGSKENNUNG
// ═══════════════════════════════════════════════════════════════════════════

/**
 * GENAU die Eingaben von `erzeugeSigePlan` — nicht weniger. Was das Blatt
 * veraendert, muss die Kennung veraendern, sonst gilt eine geaenderte Ausgabe
 * als „schon abgelegt" (Codex-Review 17.10.2026: QR und Fusszeile fehlten).
 */
export interface SigePlanStandEingabe {
    /** Der Plan, wie er GEDRUCKT wird (`projektFuerDruck`): mit wirksamer
     *  Breite und Schrift. Ein anderes Blatt ist ein anderer Stand. */
    druck: SigeProject;
    arbeitsschritte: Arbeitsschritt[];
    gefaehrdungen: Record<string, Gefaehrdung[]>;
    massnahmen: Record<string, Schutzmassnahme[]>;
    bloecke: SigeBlock[];
    /**
     * Die geladenen Piktogramm-Bilder, so wie sie an den Generator gehen.
     * Fehlt eines (Quelle nicht erreichbar), fehlt es im PDF — das ist ein
     * anderes Blatt.
     */
    piktoBilder?: Record<string, string> | null;
    /** QR-Block der Online-Einweisung; null = kein Block auf dem Plan. */
    einweisungQr?: EinweisungQrQuelle | null;
    /** Projekt-Nr. und -bezeichnung der Fusszeile. */
    fusszeile?: { projektNummer?: string | null; projektName?: string | null } | null;
}

const nachId = <T extends { id?: string }>(liste: readonly T[] | null | undefined): T[] =>
    [...(liste || [])].sort((a, b) => String(a?.id || '').localeCompare(String(b?.id || '')));

/** Ein Verzeichnis `{ elternId: Kinder[] }` in fester Reihenfolge. */
const geordnet = <T extends { id?: string }>(
    verzeichnis: Record<string, T[]> | null | undefined,
): Array<[string, T[]]> =>
    Object.keys(verzeichnis || {})
        .filter(k => (verzeichnis?.[k] || []).length > 0)
        .sort()
        .map(k => [k, nachId(verzeichnis?.[k])]);

/**
 * Der Inhaltsstand einer Planversion.
 *
 * Reihenfolgen werden festgezogen, bevor gehasht wird: der Editor haelt die
 * Gefaehrdungen in der Reihenfolge, in der die Datenbank sie lieferte, und
 * die ist nicht zugesichert. Zeitstempel der Datenbank (`created_at`,
 * `updated_at`) fallen in der Serialisierung weg — ein erneutes Speichern
 * desselben Textes ist kein neuer Stand.
 */
export const sigePlanInhaltsStand = (e: SigePlanStandEingabe): string =>
    stabilerInhaltsHash({
        plan: e.druck,
        arbeitsschritte: [...(e.arbeitsschritte || [])].sort((a, b) =>
            (Number(a?.sortierung) || 0) - (Number(b?.sortierung) || 0)
            || String(a?.id || '').localeCompare(String(b?.id || ''))),
        gefaehrdungen: geordnet(e.gefaehrdungen),
        massnahmen: geordnet(e.massnahmen),
        bloecke: nachId(e.bloecke),
        // Schluessel sortiert die Serialisierung selbst; `undefined` und `null`
        // sind dort dasselbe („kein QR").
        piktoBilder: e.piktoBilder ?? null,
        einweisungQr: e.einweisungQr ?? null,
        fusszeile: e.fusszeile ?? null,
    });

/**
 * Die Fassungskennung fuer `fassung_ref`: Versions-Id plus Inhaltsstand.
 *
 * Die Versions-Id allein reichte nicht — dieselbe Version wird weiter
 * bearbeitet und spaeter erneut abgelegt, und das ist dann eine andere
 * Fassung. Der Inhaltsstand allein reichte auch nicht: zwei Versionen mit
 * gleichem Inhalt sind zwei Zeilen im Plan und sollen es auch in der Ablage
 * sein.
 */
export const sigePlanFassungRef = (planId: string, inhaltsStand: string): string =>
    `sigeplan:${text(planId) || 'ohne-id'}:${inhaltsStand}`;

// ═══════════════════════════════════════════════════════════════════════════
// TITEL, STAND, BESCHREIBUNG
// ═══════════════════════════════════════════════════════════════════════════

type PlanKopf = Pick<SigeProject, 'plan_nr' | 'plan_datum' | 'status' | 'version_label'>;

/** Versionsname und Plandatum — die beiden Angaben, an denen man eine Fassung erkennt. */
const kennzeichen = (plan: PlanKopf, heuteIso: string): string[] => {
    const label = text(plan?.version_label);
    const tag = tagDeutsch(plan?.plan_datum) || tagDeutsch(heuteIso);
    return [label, tag ? `Stand ${tag}` : ''].filter(Boolean);
};

/**
 * Der Titel in der Ablage.
 *
 * Versionsname und Datum gehoeren IN den Titel: die Ablage sortiert nach
 * Titel, und eine Liste mit fuenfmal „SiGe-Plan 12" laesst sich nicht lesen.
 * Den Entwurf kennzeichnet die Ablage SELBST (Praefix „ENTWURF — " und Chip,
 * `unterlagenAblage.ts`); hier wird er nicht noch einmal markiert, sonst
 * stuende er zweimal im Titel.
 */
export const sigePlanAblageTitel = (plan: PlanKopf, heuteIso: string): string => {
    const nr = text(plan?.plan_nr);
    const kopf = nr ? `SiGe-Plan ${nr}` : 'SiGe-Plan';
    const teile = kennzeichen(plan, heuteIso).join(', ');
    return teile ? `${kopf} · ${teile}` : kopf;
};

/** Kurzer Stand fuer die Spalte „Stand" der Ablage: Versionsname, sonst Plandatum. */
export const sigePlanAblageStand = (plan: PlanKopf, heuteIso: string): string =>
    text(plan?.version_label) || tagDeutsch(plan?.plan_datum) || tagDeutsch(heuteIso);

export interface Blattmass {
    breiteMm: number;
    hoeheMm: number;
}

const blattText = (blatt?: Blattmass | null): string => {
    if (!blatt || !(blatt.breiteMm > 0) || !(blatt.hoeheMm > 0)) return '';
    const mm = (n: number) => Math.round(n).toLocaleString('de-DE');
    return ` Blatt ${mm(blatt.breiteMm)} × ${mm(blatt.hoeheMm)} mm.`;
};

/** Beschreibung — sagt dem Leser in der Ablage, WIE die Zeile entstanden ist. */
export const sigePlanAblageBeschreibung = (
    plan: Pick<SigeProject, 'status'>,
    anlass: SigePlanAblageAnlass,
    blatt?: Blattmass | null,
): string => {
    const status = sigePlanStatusText(plan?.status);
    const statusTeil = status ? ` Plan-Status: ${status}.` : '';
    if (anlass === 'versand') {
        return `Beim PDF-Export mit Leistungsbuchung automatisch abgelegt.${statusTeil}${blattText(blatt)}`;
    }
    if (anlass === 'freigabe') {
        return `Geprüfte Fassung, von Hand aus der Plan-Vorschau abgelegt.${blattText(blatt)}`;
    }
    // „Entwurfsstand — nicht freigegeben." haengt die Ablage selbst an.
    return `Von Hand aus der Plan-Vorschau abgelegt, kein Export, keine Leistungsbuchung.${statusTeil}${blattText(blatt)}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// AUFTRAG AN DIE ABLAGE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Soll ueberhaupt abgelegt werden?
 *
 * Nein ohne gespeichertes Projekt ('temp-id' ist die Vorbelegung eines nie
 * gespeicherten Projekts, siehe DokumentePanel) — die Storage-Policy faende
 * kein Projekt. Nein in der Nur-Ansicht: die Ablage ist ein Schreibvorgang
 * am Projekt. Der Grund wird angezeigt, nie verschluckt.
 */
export const sollSigePlanAblegen = (
    a: { projectId: string | null | undefined; isReadOnly: boolean },
): { status: 'ok' } | { status: 'fehler'; grund: string } => {
    const id = text(a.projectId);
    if (!id || id === 'temp-id') {
        return { status: 'fehler', grund: 'Das Projekt ist noch nicht gespeichert. Der Plan wurde nicht in Dokumente abgelegt.' };
    }
    if (a.isReadOnly) {
        return { status: 'fehler', grund: 'Nur-Ansicht: der Plan wurde nicht in Dokumente abgelegt.' };
    }
    return { status: 'ok' };
};

/**
 * Der Auftrag an `legeUnterlageAb` — strukturgleich mit dessen Eingabe,
 * aber hier ohne Abhaengigkeit zum Dienst, damit die Datei rein bleibt.
 */
export interface SigePlanAblageAuftrag {
    projectId: string;
    modul: 'sigeplan';
    datei: Blob;
    dateiname: string;
    titel: string;
    stand: string;
    fassungRef: string;
    anlass: SigePlanAblageAnlass;
    beschreibung: string;
    /**
     * Der Steckbrief-Anker des ERSTEN Versuchs — nur im Auftrag fuer „Erneut
     * ablegen" (`legeSigePlanAb` liefert ihn als `wiederholung`). Beim ersten
     * Versuch weggelassen; `null` heisst: der erste Versuch hatte keinen, der
     * Kern traegt dann keinen heutigen Stand nach.
     */
    anker?: AusgabeAnker | null;
}

export interface SigePlanAblageArgs {
    projectId: string;
    /** Der Plan, wie er gedruckt wurde (Breite und Schrift aufgeloest). */
    plan: SigeProject;
    datei: Blob;
    anlass: SigePlanAblageAnlass;
    fassungRef: string;
    heuteIso: string;
    blatt?: Blattmass | null;
}

export const baueSigePlanAblageAuftrag = (a: SigePlanAblageArgs): SigePlanAblageAuftrag => ({
    projectId: a.projectId,
    modul: 'sigeplan',
    datei: a.datei,
    dateiname: sigePlanDateiname(a.plan),
    titel: sigePlanAblageTitel(a.plan, a.heuteIso),
    stand: sigePlanAblageStand(a.plan, a.heuteIso),
    fassungRef: a.fassungRef,
    anlass: a.anlass,
    beschreibung: sigePlanAblageBeschreibung(a.plan, a.anlass, a.blatt),
});

/** Beschriftung des Knopfes in der Vorschau. */
export const sigePlanAblageKnopfText = (
    status: SigeProject['status'] | null | undefined,
): string => (sigePlanAblageAnlass(status) === 'freigabe'
    ? 'Fassung in Dokumente ablegen'
    : 'Entwurf in Dokumente ablegen');

/**
 * Der letzte Ablageversuch, mit der Fassung, die er meinte.
 *
 * Die Vorschau zeigt ihn nur, solange sie denselben Stand anzeigt — nach
 * einer Aenderung am Plan waere „In Dokumente abgelegt" eine Aussage ueber
 * ein anderes Blatt.
 */
export interface SigePlanAblageStand {
    fassungRef: string;
    stand: AblageStand;
    /**
     * Der gesendete Auftrag. „Erneut ablegen" schickt GENAU ihn noch einmal
     * — dieselben Bytes, dieselbe Kennung, derselbe Titel — statt das Blatt
     * aus dem inzwischen vielleicht geaenderten Plan neu zu erzeugen. `null`,
     * wenn es nie bis zum Auftrag kam.
     */
    auftrag: SigePlanAblageAuftrag | null;
}

/**
 * Der festgehaltene Auftrag zu einem Stand — oder `null`.
 *
 * „Erneut ablegen" meint den Auftrag, der gescheitert ist: dieselben Bytes,
 * dieselbe Kennung UND derselbe Anlass. Ein nach dem Export gescheiterter
 * Versand-Auftrag wird also als Versand wiederholt, auch wenn der Knopf in
 * der Vorschau sonst „Entwurf" hiesse (Codex-Review 17.10.2026). Zeigt die
 * Vorschau inzwischen einen anderen Stand, gibt es nichts zu wiederholen.
 */
export const wiederverwendbarerAuftrag = (
    letzter: SigePlanAblageStand | null | undefined,
    fassungRef: string,
): SigePlanAblageAuftrag | null =>
    (letzter?.auftrag && letzter.fassungRef === fassungRef ? letzter.auftrag : null);
